<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTenantStore } from '@/stores/tenant'
import type { TenantId } from '@/config/tenants'
import mwLogo from '@/assets/mw_logo.svg'
import mwLogoDark from '@/assets/mw_logo_dark.svg'
import plccLogo from '@/assets/plcc_logo.svg'
import plccLogoDark from '@/assets/plcc_logo_dark.svg'

/* The brand block: tenant logo + "Partner Portal" wordmark. Used in both
   layouts so it is unmistakable that this is the official partner portal.
   Light/dark logo variants are swapped by the browser via prefers-color-scheme
   on the <picture> source, so no JS theme state is needed here. */
withDefaults(defineProps<{ size?: 'md' | 'lg' }>(), { size: 'md' })

const { t } = useI18n()
const tenant = useTenantStore()

const logos: Record<TenantId, { light: string; dark: string }> = {
  maluwilz: { light: mwLogo, dark: mwLogoDark },
  plcc: { light: plccLogo, dark: plccLogoDark },
}

const logo = computed(() => logos[tenant.current.id])
</script>

<template>
  <span class="brand" :class="`brand--${size}`">
    <picture>
      <source media="(prefers-color-scheme: dark)" :srcset="logo.dark" />
      <img class="brand__logo" :src="logo.light" :alt="tenant.current.name" />
    </picture>
    <span class="brand__portal">{{ t(`portal.${tenant.current.id}`) }}</span>
  </span>
</template>

<style scoped>
.brand {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
}

.brand__logo {
  display: block;
  height: 20px;
  width: auto;
}

.brand__portal {
  font-size: var(--font-size-xs);
  text-transform: uppercase;
  letter-spacing: var(--tracking-heading);
  color: var(--color-text-muted);
  white-space: nowrap;
}

.brand--lg .brand__logo {
  height: 30px;
}

.brand--lg .brand__portal {
  font-size: var(--font-size-sm);
}
</style>
